import React, { useEffect, useState } from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	CardContent,
	Link,
	Stack,
} from '@mui/material';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import GetSupport from '../GetSupport';
import { MixpanelWrapper } from '../MixPanelWrapper';
import { colours } from '../../shared/colors';
import CreateQuoteCardHeader from '../CreateQuoteCardHeader';

const FAQAccordion = ({ title, contents }: { title: string; contents: string | JSX.Element }) => {
	const typographyStyle: React.CSSProperties = { whiteSpace: 'pre-line' };

	return (
		<Accordion disableGutters defaultExpanded={true} elevation={0}>
			<AccordionSummary>
				<Typography variant='h5'>{title}</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Typography variant='subtitle1' style={typographyStyle}>
					{contents}
				</Typography>
			</AccordionDetails>
		</Accordion>
	);
};

const UnderwritingLink = () => (
	<Link
		href='mailto:underwriting@deltainsurance.com.au'
		target='_blank'
		rel='noopener'
		color='inherit'
	>
		{'underwriting@deltainsurance.com.au'}
	</Link>
);

const SOFT_LAUNCH = true;

/**
 * Resources component for displaying frequently asked questions.
 *
 * @returns {JSX.Element} - React JSX element representing the resources component.
 */
const Resources = () => {
	useEffect(() => {
		MixpanelWrapper.track('Resources');
	}, []);

	return (
		<Card
			elevation={0}
			sx={{
				borderRadius: '7px',
				mb: '3em',
			}}
		>
			<CardContent>
				<Stack direction='row' justifyContent='space-between' alignItems='center'>
					<Typography variant='quoteTabHeader'>{'Frequently asked questions'}</Typography>
				</Stack>
				<Stack
					spacing={2}
					pt='1em'
					sx={{
						'& .MuiButtonBase-root': {
							borderTop: `7px solid ${colours.primaryLight.main}`,
						},
						'& .MuiTypography-h3': {
							fontWeight: 700,
							fontSize: '40px',
							color: colours.darkGrey.main,
						},
						'& .MuiTypography-h5': {
							fontWeight: 700,
							color: colours.darkGrey.main,
						},
						'& .MuiTypography-subtitle1': {
							fontWeight: 500,
							fontSize: '15px',
						},
						'& .MuiPaper-root': {
							border: 'none',
						},
					}}
				>
					{/* <FAQAccordion
						title={"What products are available on Delta's portal?"}
						contents={
							SOFT_LAUNCH
								? `The following products are available:
                        - Product Recall Insurance
                        
                        To see what products are available for your Insured, you can enter the Insured's details and review the 'Select Products' screen to see the basket of products available for quoting based on a range of factors including the Insured's occupation, turnover, employee numbers, and entity type.`
								: `The following products are available:
                        - Professional Indemnity Insurance
                        - Cyber Protection Insurance
                        - Management Liability Insurance
                        - General Liability Insurance
                        - Commercial Legal Expenses Insurance
                        - Product Recall Insurance
        
                      To see what products are available for your Insured, you can enter the Insured's details and review the 'Select Products' screen to see the basket of products available for quoting based on a range of factors including the Insured's occupation, turnover, employee numbers and entity type.`
						}
					/> */}
					{/* <FAQAccordion
						title="What is the Insured's maximum turnover that I can quote in the portal?"
						contents={`The maximum turnover varies by product and is as follows:
                    ${
						SOFT_LAUNCH
							? '- $10m is the maximum turnover for Product Recall'
							: `- $20m is the maximum turnover for Management Liability
                    - $10m is the maximum turnover for Product Recall
                    - $5m is the maximum turnover for Professional Indemnity, Cyber Protection, General Liability & Commercial Legal Expenses
                    `
					}`}
					/> */}
					{/* <FAQAccordion
						title='What are the maximum limits available for Insureds in the portal?'
						contents={`The maximum policy limit varies by product and is as follows:
                    ${
						SOFT_LAUNCH
							? '- $500K is the maximum limit for Product Recall'
							: `- $20m is the maximum limit for General Liability
                    - $5m is the maximum limit for Professional Indemnity, Management Liability & Commercial Legal Expenses
                    - $500K is the maximum limit for Product Recall
                    - $100k is the maximum limit for Commercial Legal Expenses`
					}`}
					/> */}
					{!SOFT_LAUNCH && (
						<>
							<FAQAccordion
								title='Can I purchase General Liability as a standalone policy?'
								contents={`No, at this stage we only offer General Liability to be purchased alongside Professional Indemnity. We will be looking to offer a standalone option at a future date.`}
							/>
							<FAQAccordion
								title='Can I purchase Commercial Legal Expenses as a standalone policy?'
								contents={`No, at this stage we only offer Commercial Legal Expenses to be purchased alongside Professional Indemnity. This product is only available to Insureds whose business entity type is a sole trader or partnership. If the Insured is a private or unlisted public company then they will be able to purchase Commercial Legal Expenses as part of the Management Liability package.`}
							/>
							<FAQAccordion
								title='Management Liability - are there any mandatory coverage sections?'
								contents={`Yes, the Directors & Officers coverage section is a mandatory coverage section when the Insured is purchasing a Management Liability package. All of the other coverage sections will be included within a standard quote but the Insured can elect to remove some or all of these additional coverage sections.`}
							/>
						</>
					)}
					<FAQAccordion
						title='How do I select the occupation for the Insured?'
						contents={
							<>
								<span style={{ paddingRight: '0.25em' }}>{`When creating a quote, you can search for and select the occupation from a dropdown box in the occupation field.\n
  If you are unable to find a suitable occupation for the Insured you can contact the underwriting team`}</span>
								<UnderwritingLink />
								{`, who will assist.\n
  If the Insured undertakes a broad range of occupations, then select the occupation that represents the largest percentage of the Insured's total turnover. There will be an additional question further in the quote journey where you can disclose the additional activities. This will generate a referral that the underwriting team will review.`}
							</>
						}
					/>
					{!SOFT_LAUNCH && (
						<FAQAccordion
							title='Will the portal provide indicative premiums?'
							contents={`After completing the Insured's details, you will be able to see which products are available based on the Insured's occupation. When you select each or all of the products then you will be able to see an indicative base premium in the 'Quote Summary' box in the portal.`}
						/>
					)}
					<FAQAccordion
						title='Can I purchase a policy that incepts on a future date?'
						contents={`Yes, a policy can be purchased up to a maximum of 45 days in the future.`}
					/>
					<FAQAccordion
						title='What is the minimum and maximum policy periods that can be selected?'
						contents={`The minimum policy period that can be selected is 9 months and the maximum policy period that can be selected is 16 months.`}
					/>
					<FAQAccordion
						title='How long will a new business quote remain valid for?'
						contents={`A new business quote will be valid for a period of 30 days commencing from the date of quotation.`}
					/>
					<FAQAccordion
						title='How do I refer a quote to the underwriting team?'
						contents={`When a referral is triggered in the portal, this will show in the 'Quote Summary' field in the Broker Portal and it will show which product is a referral. Once you have completed all the Product Questions, you will be able to use the 'Refer Client' button in the 'Quote Summary' box to refer the risk to Delta's underwriting team.\n
                    You will be able to send additional information and attachments with your referral.\n
                    Once you have referred the risk, you will receive a confirmation via email which will also include the contact of the underwriting team in case you want to get in touch with the team to discuss the referral.`}
					/>
					<FAQAccordion
						title='Can I amend the broker commission on a quote?'
						contents={`Yes, you will be able to reduce the commission from the standard commission for the product down to zero.`}
					/>
					<FAQAccordion
						title='Do I need to provide a completed proposal form?'
						contents={`We do not require a proposal form for a risk to be bound in the portal. If you would like to upload a proposal form, please attach it to the online proposal for our underwriter to review.`}
					/>
					<FAQAccordion
						title='Do I need to send Delta a closing once I have bound a quote?'
						contents={
							<>
								<span style={{ paddingRight: '0.25em' }}>{
									'No, we do not require a closing. If you would still prefer to send a closing, please email it to'
								}</span>
								<Link
									href='mailto:closings@deltainsurance.com.au'
									target='_blank'
									rel='noopener'
									onClick={() => MixpanelWrapper.track('Get Support')}
									color='inherit'
								>
									{'closings@deltainsurance.com.au'}
								</Link>
							</>
						}
					/>
					<FAQAccordion
						title='Where do I get a certificate of currency (COC) from the portal?'
						contents={`A certificate of currency is available for download once your policy is bound.`}
					/>
					<FAQAccordion
						title='Can I do a mid-term endorsement on a bound policy in the portal?'
						contents={
							<>
								<span style={{ paddingRight: '0.25em' }}>{`No. If you would like to make a change to a bound policy then please contact the underwriting team`}</span>
								<UnderwritingLink />
							</>
						}
					/>
					<FAQAccordion
						title='Can I cancel a policy in the Delta portal?'
						contents={
							<>
								<span style={{ paddingRight: '0.25em' }}>{`No, you will not be able to cancel a policy through the Delta portal. Please contact the underwriting team who will be able to assist with the cancellation request`}</span>
								<UnderwritingLink />
							</>
						}
					/>
					<FAQAccordion
						title='Can I lodge a claim on the Delta portal?'
						contents={
							<>
								<span style={{ paddingRight: '0.25em' }}>{`No, you cannot lodge a claim on the Delta portal. For more details on lodging claims, please visit our website: `}</span>
								<Link
									href='https://deltainsurance.com.au/pages/claims'
									target='_blank'
									rel='noopener'
									onClick={() => MixpanelWrapper.track('Get Support')}
									color='inherit'
								>
									{'https://deltainsurance.com.au/pages/claims'}
								</Link>
							</>
						}
					/>
				</Stack>
			</CardContent>
		</Card>
	);
};

export default Resources;
